import React from 'react';

const SKILL_CAT_LABELS = {
  legal: 'Legal', transactions: 'Transactions', litigation: 'Litigation',
  compliance: 'Compliance', researchTools: 'Research Tools',
  technology: 'Technology', softSkills: 'Soft Skills', other: 'Other',
};

const PREVIEW_THEMES = {
  classic: {
    headerBg: '#1a365d', headerPad: true, nameColor: '#ffffff',
    barEnrolColor: 'rgba(255,255,255,0.7)', contactColor: 'rgba(255,255,255,0.85)',
    topBar: null, align: 'left',
    sectionTitleColor: '#2b6cb0', dividerColor: '#e2e8f0', dividerHeight: '1px',
    entryTitleColor: '#1a365d', catLabelColor: '#2b6cb0', leftBar: null,
  },
  modern: {
    headerBg: '#eef2ff', headerPad: true, nameColor: '#0f172a',
    barEnrolColor: '#64748b', contactColor: '#475569',
    topBar: '#4f46e5', align: 'left',
    sectionTitleColor: '#4f46e5', dividerColor: '#c7d2fe', dividerHeight: '1px',
    entryTitleColor: '#0f172a', catLabelColor: '#4f46e5', leftBar: '#4f46e5',
  },
  minimal: {
    headerBg: null, headerPad: false, nameColor: '#111111',
    barEnrolColor: '#555555', contactColor: '#444444',
    topBar: null, align: 'center',
    sectionTitleColor: '#111111', dividerColor: '#bbbbbb', dividerHeight: '1px',
    entryTitleColor: '#111111', catLabelColor: '#333333', leftBar: null,
  },
  executive: {
    headerBg: '#1c1917', headerPad: true, nameColor: '#d4a843',
    barEnrolColor: 'rgba(255,255,255,0.65)', contactColor: '#d6d3d1',
    topBar: null, align: 'left',
    sectionTitleColor: '#1c1917', dividerColor: '#b5853a', dividerHeight: '2px',
    entryTitleColor: '#1c1917', catLabelColor: '#b5853a', leftBar: null,
  },
};

function getThm(template, r) {
  if (template === 'custom' && r) {
    const hBg = r.customHeaderBg || '#1a365d';
    const acc = r.customAccentColor || '#c89b3c';
    // simple luminance check to decide text colors
    const hex = hBg.replace('#', '');
    const rgb = [parseInt(hex.slice(0,2),16), parseInt(hex.slice(2,4),16), parseInt(hex.slice(4,6),16)];
    const lum = (0.299*rgb[0] + 0.587*rgb[1] + 0.114*rgb[2]) / 255;
    const light = lum > 0.5;
    return {
      headerBg: hBg, headerPad: true,
      nameColor:      light ? '#111111' : '#ffffff',
      barEnrolColor:  light ? '#555555' : 'rgba(255,255,255,0.75)',
      contactColor:   light ? '#444444' : 'rgba(255,255,255,0.85)',
      topBar: null, align: 'left',
      sectionTitleColor: acc, dividerColor: acc,
      dividerHeight: '1px', entryTitleColor: hBg,
      catLabelColor: acc, leftBar: null,
    };
  }
  return PREVIEW_THEMES[template] || PREVIEW_THEMES.classic;
}

export default function ResumePreview({ resume }) {
  if (!resume) return null;
  return resume.resumeType === 'internship'
    ? <InternshipTemplate r={resume} />
    : <JobPlacementTemplate r={resume} />;
}

// ── Job Placement Template ─────────────────────────────────────────────────────

function JobPlacementTemplate({ r }) {
  const pi   = r.personalInfo || {};
  const thm  = getThm(r.template, r);
  const cats = ['legal','transactions','litigation','compliance','researchTools','technology','softSkills'];
  const hasSkills = cats.some(c => r.skillCategories?.[c]?.length > 0);

  return (
    <div style={st.paper}>
      <Header pi={pi} isInt={false} thm={thm} />

      {r.summary && <Sec title="Professional Summary" thm={thm}><p style={st.body}>{r.summary}</p></Sec>}

      {r.experience?.length > 0 && (
        <Sec title="Experience" thm={thm}>
          {r.experience.map((exp, i) => (
            <div key={i} style={st.entry}>
              <div style={st.entryHeader}>
                <div>
                  <span style={{ ...st.entryTitle, color: thm.entryTitleColor }}>{exp.position}</span>
                  {exp.company && <span style={st.entrySubtitle}>- {exp.company}</span>}
                </div>
                <span style={st.entryDate}>{[exp.startDate, exp.current ? 'Present' : exp.endDate].filter(Boolean).join(' – ')}</span>
              </div>
              {exp.description && <p style={st.body}>{exp.description}</p>}
            </div>
          ))}
        </Sec>
      )}

      {r.education?.length > 0 && (
        <Sec title="Education" thm={thm}>
          {r.education.map((edu, i) => (
            <div key={i} style={st.entry}>
              <div style={st.entryHeader}>
                <span style={{ ...st.entryTitle, color: thm.entryTitleColor }}>{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</span>
                <span style={st.entryDate}>{[edu.startDate, edu.endDate].filter(Boolean).join(' – ')}</span>
              </div>
              <p style={st.body}>{edu.institution}{edu.grade ? ` - ${edu.grade}` : ''}</p>
            </div>
          ))}
        </Sec>
      )}

      {r.practiceAreaFocus && <Sec title="Practice Area Focus & Career Direction" thm={thm}><p style={st.body}>{r.practiceAreaFocus}</p></Sec>}

      {hasSkills && (
        <Sec title="Skills" thm={thm}>
          {cats.filter(c => r.skillCategories?.[c]?.length > 0).map(cat => (
            <div key={cat} style={{ marginBottom: '6px' }}>
              <span style={{ ...st.catLabel, color: thm.catLabelColor }}>{SKILL_CAT_LABELS[cat]}: </span>
              <span style={st.body}>{r.skillCategories[cat].join(' • ')}</span>
            </div>
          ))}
        </Sec>
      )}

      {r.languages?.length > 0 && (
        <Sec title="Languages" thm={thm}>
          <table style={st.table}>
            <thead><tr><th style={st.th}>Language</th><th style={st.th}>Proficiency</th></tr></thead>
            <tbody>{r.languages.map((l, i) => <tr key={i}><td style={st.td}>{l.language}</td><td style={st.td}>{l.proficiency}</td></tr>)}</tbody>
          </table>
        </Sec>
      )}

      {r.mootCourts?.length > 0 && (
        <Sec title="Moot Court & Academic Competitions" thm={thm}>
          <table style={st.table}>
            <thead><tr><th style={st.th}>Competition</th><th style={st.th}>Round / Rank</th><th style={st.th}>Year</th></tr></thead>
            <tbody>{r.mootCourts.map((mc, i) => <tr key={i}><td style={st.td}>{mc.competition}</td><td style={st.td}>{mc.roundOrRank}</td><td style={st.td}>{mc.year}</td></tr>)}</tbody>
          </table>
        </Sec>
      )}

      {r.publications?.length > 0 && (
        <Sec title="Publications" thm={thm}>
          {r.publications.map((p, i) => <p key={i} style={{ ...st.body, marginBottom: '4px' }}>• {p}</p>)}
        </Sec>
      )}

      {r.additionalQualifications && <Sec title="Additional Qualifications" thm={thm}><p style={st.body}>{r.additionalQualifications}</p></Sec>}

      {r.professionalMemberships?.length > 0 && (
        <Sec title="Professional Memberships" thm={thm}>
          <p style={st.body}>{r.professionalMemberships.join(' • ')}</p>
        </Sec>
      )}

      {r.targetType === 'in-house' && r.commercialExposure && (
        <Sec title="Commercial & Business Exposure" thm={thm}><p style={st.body}>{r.commercialExposure}</p></Sec>
      )}

      {r.awards?.length > 0 && (
        <Sec title="Awards & Recognitions" thm={thm}>
          {r.awards.map((a, i) => <p key={i} style={{ ...st.body, marginBottom: '4px' }}>• {a}</p>)}
        </Sec>
      )}

      {r.professionalInterests && <Sec title="Professional Interests" thm={thm}><p style={st.body}>{r.professionalInterests}</p></Sec>}
    </div>
  );
}

// ── Internship Template ────────────────────────────────────────────────────────

function InternshipTemplate({ r }) {
  const pi   = r.personalInfo || {};
  const thm  = getThm(r.template, r);
  const cats = ['legal','researchTools','technology','other'];
  const hasSkills = cats.some(c => r.skillCategories?.[c]?.length > 0);

  return (
    <div style={st.paper}>
      <Header pi={pi} isInt={true} thm={thm} />

      {r.aboutMe && <Sec title="About Me" thm={thm}><p style={st.body}>{r.aboutMe}</p></Sec>}

      {r.education?.length > 0 && (
        <Sec title="Education" thm={thm}>
          {r.education.map((edu, i) => (
            <div key={i} style={st.entry}>
              <div style={st.entryHeader}>
                <span style={{ ...st.entryTitle, color: thm.entryTitleColor }}>{edu.degree}{edu.field ? ` in ${edu.field}` : ''}</span>
                <span style={st.entryDate}>{[edu.startDate, edu.endDate].filter(Boolean).join(' – ')}</span>
              </div>
              <p style={st.body}>{edu.institution}{edu.grade ? ` - ${edu.grade}` : ''}</p>
            </div>
          ))}
        </Sec>
      )}

      {r.experience?.length > 0 && (
        <Sec title="Internship Experience" thm={thm}>
          {r.experience.map((exp, i) => (
            <div key={i} style={st.entry}>
              <div style={st.entryHeader}>
                <div>
                  <span style={{ ...st.entryTitle, color: thm.entryTitleColor }}>{exp.position}</span>
                  {exp.company && <span style={st.entrySubtitle}>- {exp.company}</span>}
                </div>
                <span style={st.entryDate}>{[exp.startDate, exp.current ? 'Present' : exp.endDate].filter(Boolean).join(' – ')}</span>
              </div>
              {exp.description && <p style={st.body}>{exp.description}</p>}
            </div>
          ))}
        </Sec>
      )}

      {r.mootCourts?.length > 0 && (
        <Sec title="Moot Court & Academic Competitions" thm={thm}>
          <table style={st.table}>
            <thead><tr><th style={st.th}>Competition</th><th style={st.th}>Round / Rank</th><th style={st.th}>Year</th></tr></thead>
            <tbody>{r.mootCourts.map((mc, i) => <tr key={i}><td style={st.td}>{mc.competition}</td><td style={st.td}>{mc.roundOrRank}</td><td style={st.td}>{mc.year}</td></tr>)}</tbody>
          </table>
        </Sec>
      )}

      {r.publications?.length > 0 && (
        <Sec title="Publications" thm={thm}>
          {r.publications.map((p, i) => <p key={i} style={{ ...st.body, marginBottom: '4px' }}>• {p}</p>)}
        </Sec>
      )}

      {hasSkills && (
        <Sec title="Skills" thm={thm}>
          {cats.filter(c => r.skillCategories?.[c]?.length > 0).map(cat => (
            <div key={cat} style={{ marginBottom: '6px' }}>
              <span style={{ ...st.catLabel, color: thm.catLabelColor }}>{SKILL_CAT_LABELS[cat]}: </span>
              <span style={st.body}>{r.skillCategories[cat].join(' • ')}</span>
            </div>
          ))}
        </Sec>
      )}

      {r.languages?.length > 0 && (
        <Sec title="Languages" thm={thm}>
          <table style={st.table}>
            <thead><tr><th style={st.th}>Language</th><th style={st.th}>Proficiency</th></tr></thead>
            <tbody>{r.languages.map((l, i) => <tr key={i}><td style={st.td}>{l.language}</td><td style={st.td}>{l.proficiency}</td></tr>)}</tbody>
          </table>
        </Sec>
      )}

      {r.collegeActivities?.length > 0 && (
        <Sec title="College Activities" thm={thm}>
          {r.collegeActivities.map((act, i) => (
            <div key={i} style={st.entry}>
              <div style={st.entryHeader}>
                <div>
                  <span style={{ ...st.entryTitle, color: thm.entryTitleColor }}>{act.activity}</span>
                  {act.role && <span style={st.entrySubtitle}>- {act.role}</span>}
                </div>
                {act.year && <span style={st.entryDate}>{act.year}</span>}
              </div>
            </div>
          ))}
        </Sec>
      )}

      {r.additionalQualifications && <Sec title="Additional Qualifications" thm={thm}><p style={st.body}>{r.additionalQualifications}</p></Sec>}

      {r.areasOfLegalInterest?.length > 0 && (
        <Sec title="Areas of Legal Interest" thm={thm}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {r.areasOfLegalInterest.map((a, i) => (
              <span key={i} style={st.tag}>{a}</span>
            ))}
          </div>
        </Sec>
      )}
    </div>
  );
}

// ── Shared components ──────────────────────────────────────────────────────────

function Header({ pi, isInt, thm }) {
  const hasColorBg = thm.headerBg && thm.headerBg !== 'transparent';

  const wrapStyle = hasColorBg ? {
    background: thm.headerBg,
    margin: '-32px -36px 20px',
    padding: '22px 36px 16px',
    textAlign: thm.align,
    ...(thm.topBar ? { borderTop: `4px solid ${thm.topBar}` } : {}),
  } : {
    textAlign: 'center',
    paddingBottom: '14px',
    borderBottom: `1px solid ${thm.dividerColor}`,
    marginBottom: '20px',
  };

  return (
    <div style={wrapStyle}>
      <h1 style={{ ...st.name, color: thm.nameColor, textAlign: thm.align }}>{pi.fullName || 'Your Name'}</h1>
      {!isInt && pi.barEnrollment && <p style={{ ...st.barEnrol, color: thm.barEnrolColor }}>Bar Enrolment No. {pi.barEnrollment}</p>}
      <div style={{ ...st.contactRow, justifyContent: thm.align === 'center' ? 'center' : 'flex-start' }}>
        {[pi.email, pi.phone, pi.location].filter(Boolean).map((v, i) => (
          <span key={i} style={{ ...st.contactItem, color: thm.contactColor }}>
            {i > 0 && <span style={{ ...st.dot, color: thm.contactColor }}>•</span>}{v}
          </span>
        ))}
      </div>
      {pi.linkedin && <div style={{ ...st.linksRow, color: thm.contactColor }}>{pi.linkedin}</div>}
    </div>
  );
}

function Sec({ title, children, thm }) {
  const titleEl = (
    <h2 style={{ ...st.sectionTitle, color: thm?.sectionTitleColor }}>{title}</h2>
  );
  return (
    <div style={st.section}>
      {thm?.leftBar ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '3px' }}>
          <div style={{ width: '3px', height: '13px', background: thm.leftBar, borderRadius: '1px', flexShrink: 0, marginTop: '1px' }} />
          {titleEl}
        </div>
      ) : titleEl}
      <div style={{ ...st.divider, background: thm?.dividerColor, height: thm?.dividerHeight || '1px' }} />
      {children}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const st = {
  paper: {
    background: '#fff', padding: '32px 36px', minHeight: '842px',
    fontFamily: "'Georgia', serif", fontSize: '13px', color: '#1a202c',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
  },
  name:   { fontSize: '26px', fontWeight: '700', marginBottom: '4px', fontFamily: 'Arial, sans-serif' },
  barEnrol: { fontSize: '11px', marginBottom: '4px' },
  contactRow: { display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '12px' },
  contactItem: { display: 'flex', alignItems: 'center', gap: '4px' },
  dot:   { fontSize: '10px' },
  linksRow: { fontSize: '11px', marginTop: '4px' },
  section: { marginBottom: '16px' },
  sectionTitle: { fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '3px', fontFamily: 'Arial, sans-serif' },
  divider: { height: '1px', marginBottom: '9px' },
  entry:       { marginBottom: '10px' },
  entryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '4px', marginBottom: '2px' },
  entryTitle:    { fontWeight: '700', fontSize: '13px' },
  entrySubtitle: { color: '#4a5568', fontSize: '13px' },
  entryDate:     { fontSize: '11px', color: '#718096', whiteSpace: 'nowrap' },
  body: { color: '#2d3748', lineHeight: '1.6', marginTop: '2px', fontSize: '12px' },
  catLabel: { fontWeight: '700', fontSize: '12px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '12px' },
  th: { textAlign: 'left', padding: '5px 8px', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', color: '#4a5568', fontWeight: '700', fontSize: '11px' },
  td: { padding: '5px 8px', borderBottom: '1px solid #f0f4f8', color: '#2d3748', verticalAlign: 'top' },
  tag: { background: '#ebf8ff', border: '1px solid #bee3f8', color: '#2b6cb0', padding: '3px 10px', borderRadius: '12px', fontSize: '12px' },
};
